import React, { useState, useCallback } from 'react';

export default function PageHero({ eyebrow, title, accent, desc, accentColor, image }) {
  const ac = accentColor || 'var(--orange)';
  const hasImage = !!image;
  const [loaded, setLoaded] = useState(!hasImage);
  const onLoad = useCallback(() => setLoaded(true), []);

  if (hasImage) {
    return (
      <section className="relative overflow-hidden" style={{ height: '120svh', minHeight: '700px' }} data-nav-theme="dark">
        {/* Shimmer skeleton */}
        <div className={`absolute inset-0 bg-[var(--ink)] transition-opacity duration-700 ${loaded ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
          <div className="absolute inset-0 shimmer-bg"/>
        </div>

        {/* Background image */}
        <img
          src={image}
          alt=""
          className={`absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-700 ${loaded ? 'opacity-100' : 'opacity-0'}`}
          loading="eager"
          fetchPriority="high"
          onLoad={onLoad}
        />

        {/* Gradient overlays behind text — strong, matching About Us */}
        <div className="absolute inset-0 bg-gradient-to-r from-[var(--ink)] from-15% via-[var(--ink)]/75 via-50% to-[var(--ink)]/30"/>
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--ink)]/60 via-transparent to-[var(--ink)]/20"/>
        <div className="absolute inset-0 noise opacity-20 pointer-events-none"/>

        {/* Content */}
        <div className="relative px-6 md:px-10 lg:px-14 pt-[calc(84px+5rem)] lg:pt-[calc(84px+7rem)] pb-20 lg:pb-28 max-w-[1500px] mx-auto">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/25 mb-6 reveal">
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: ac }}/>
              <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-white">{eyebrow}</span>
            </div>
            <h1 className="font-display text-[clamp(40px,5.8vw,84px)] leading-[1.02] font-bold tracking-tight reveal" style={{ color: '#fff', transitionDelay: '120ms' }}>
              {title}<br/>
              <span className="font-serif-it" style={{ color: ac }}>{accent}</span>
            </h1>
            {desc && (
              <p className="mt-8 text-[17px] md:text-[19px] text-white/90 font-medium max-w-2xl leading-[1.75] reveal" style={{ transitionDelay: '280ms' }}>
                {desc}
              </p>
            )}
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-60">
          <div className="w-px h-12 bg-white/50 animate-pulse"/>
          <span className="text-[10px] uppercase tracking-[0.2em] text-white/70">Scroll</span>
        </div>
      </section>
    );
  }

  return (
    <section className="relative overflow-hidden bg-[var(--cream)] border-b border-[var(--line)]">
      <div className="absolute inset-0 grid-bg opacity-50 pointer-events-none"/>
      <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-[var(--orange)]/10 blur-3xl pointer-events-none"/>
      <div className="absolute inset-0 noise pointer-events-none"/>
      <div className="relative px-6 md:px-10 lg:px-14 py-20 lg:py-28 max-w-[1500px] mx-auto">
        <div className="max-w-4xl">
          <div className="text-[11px] uppercase tracking-[0.22em] font-semibold mb-5" style={{ color: ac }}>{eyebrow}</div>
          <h1 className="font-display text-[clamp(40px,6vw,84px)] leading-[1.02] font-bold tracking-tight reveal" style={{ transitionDelay: '60ms' }}>
            {title}<br/>
            <span className="font-serif-it" style={{ color: ac }}>{accent}</span>
          </h1>
          {desc && <p className="mt-7 text-[17px] text-[var(--ink)]/85 font-medium max-w-2xl leading-relaxed reveal" style={{ transitionDelay: '220ms' }}>{desc}</p>}
        </div>
      </div>
    </section>
  );
}
